import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Building2, CheckCircle2, MessageSquare, Phone, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

export default function SellProperty() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    propertyType: "daire",
    ilce: "Seyhan",
    mahalle: "Gürselpaşa",
    roomCount: "3+1",
    expectedPrice: "",
    notes: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // GA4 Event Trigger
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "generate_lead", {
        event_category: "Property",
        event_label: "Sell Property Form"
      });
    }

    toast({
      title: "WhatsApp mesajınız hazırlanıyor",
      description: "Talebinizi iletmek için açılan WhatsApp penceresinde mesajı gönderin.",
    });

    const waText = encodeURIComponent(
      `Merhaba Corner Gayrimenkul, Adana'daki gayrimenkulümü satmak istiyorum.\n\n` +
      `Ad Soyad: ${formData.fullName}\n` +
      `Telefon: ${formData.phone}\n` +
      `Gayrimenkul Tipi: ${formData.propertyType}\n` +
      `Lokasyon: Adana / ${formData.ilce} / ${formData.mahalle}\n` +
      `Oda Sayısı: ${formData.roomCount}\n` +
      `Tahmini Fiyat: ${formData.expectedPrice} TL\n` +
      `Not: ${formData.notes}`
    );

    window.open(`https://wa.me/905335487801?text=${waText}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Adana'da Evimi Satmak İstiyorum | Gayrimenkulünü Sat"
        description="Adana genelinde ev, daire, villa veya arsanız için satış danışmanlığı talep edin. Mülk bilgilerinizi Corner Gayrimenkul ile paylaşın."
        canonicalPath="/gayrimenkulumu-sat"
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Gayrimenkulünüzü Satın" }]} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start my-8">
          {/* Info Side */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
              Mülk Sahiplerine Özel Hizmet
            </div>
            <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
              Adana'da Gayrimenkulünüzü Güvenle ve Hızla Satın
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Corner Gayrimenkul profesyonel ekspertiz, hedef alıcı kitlesine ulaşan dijital pazarlama ağı ve şeffaf süreç yönetimi ile mülkünüzün gerçek değerinde satılmasını sağlar.
            </p>

            <div className="space-y-4 pt-4">
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 text-green-600 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground">Ücretsiz Ekspertiz & Değer Tanımlama</h4>
                  <p className="text-sm text-muted-foreground">Adana emlak piyasasındaki en güncel emsal verilerle gayrimenkulünüzün doğru satış fiyatını belirliyoruz.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
                  <Building2 className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground">Profesyonel Fotoğraf & İlan Tanıtımı</h4>
                  <p className="text-sm text-muted-foreground">Portföyünüzü Corner web sitemizde, Sahibinden mağazamızda ve sosyal mecrada öne çıkarıyoruz.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground">Nitelikli Alıcı Portföyü</h4>
                  <p className="text-sm text-muted-foreground">Kredi ve nakit durumu onaylanmış ciddi alıcılarla mülkünüzü buluşturuyoruz.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Lead Form Side */}
          <Card className="p-6 sm:p-8 bg-card border border-border shadow-xl rounded-3xl">
            <h3 className="text-2xl font-extrabold text-foreground mb-2">
              Gayrimenkul Değerlendirme Formu
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Aşağıdaki formu doldurun, uzman danışmanımız sizi hemen arasın.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Adınız Soyadınız *</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="Örn: Ahmet Yılmaz"
                  className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Telefon Numaranız *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Telefon numaranız"
                  className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Gayrimenkul Tipi</label>
                  <select
                    value={formData.propertyType}
                    onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  >
                    <option value="daire">Daire</option>
                    <option value="villa">Müstakil Villa</option>
                    <option value="arsa">Arsa</option>
                    <option value="isyeri">İşyeri / Dükkan</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">İlçe</label>
                  <select
                    value={formData.ilce}
                    onChange={(e) => setFormData({ ...formData, ilce: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  >
                    <option value="Seyhan">Seyhan</option>
                    <option value="Çukurova">Çukurova</option>
                    <option value="Sarıçam">Sarıçam</option>
                    <option value="Yüreğir">Yüreğir</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Mahalle</label>
                  <input
                    type="text"
                    value={formData.mahalle}
                    onChange={(e) => setFormData({ ...formData, mahalle: e.target.value })}
                    placeholder="Örn: Gürselpaşa"
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Tahmini Fiyat Beklentiniz</label>
                  <input
                    type="text"
                    value={formData.expectedPrice}
                    onChange={(e) => setFormData({ ...formData, expectedPrice: e.target.value })}
                    placeholder="Örn: 3.500.000 TL"
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Açıklama / Detaylar</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Dairenin özellikleri, bina yaşı, site adı vb."
                  className="w-full p-3 rounded-lg border border-input bg-background text-sm resize-none"
                />
              </div>

              <Button type="submit" size="lg" className="w-full font-bold text-base shadow-lg">
                <Send className="h-4 w-4 mr-2" /> Değerlendirme Talebi Gönder
              </Button>
            </form>
          </Card>
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
